// src/admin/components/EditOrganizationDialog.tsx
import { Organization } from '@/types/organization';
import { apiFetch } from '@/util/apiClient';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

const MONTHS = [
  { value: 1, label: 'January', days: 31 },
  { value: 2, label: 'February', days: 28 },
  { value: 3, label: 'March', days: 31 },
  { value: 4, label: 'April', days: 30 },
  { value: 5, label: 'May', days: 31 },
  { value: 6, label: 'June', days: 30 },
  { value: 7, label: 'July', days: 31 },
  { value: 8, label: 'August', days: 31 },
  { value: 9, label: 'September', days: 30 },
  { value: 10, label: 'October', days: 31 },
  { value: 11, label: 'November', days: 30 },
  { value: 12, label: 'December', days: 31 },
];

function parseFiscalYearStart(value?: string | null): {
  month: number | '';
  day: number | '';
} {
  if (!value) return { month: '', day: '' };
  // value is "YYYY-MM-DD"
  const parts = value.split('-');
  if (parts.length !== 3) return { month: '', day: '' };
  const month = parseInt(parts[1], 10);
  const day = parseInt(parts[2], 10);
  if (isNaN(month) || isNaN(day)) return { month: '', day: '' };
  return { month, day };
}

const MEASUREMENT_SYSTEMS = [
  { value: 'imperial', label: 'Imperial' },
  { value: 'metric', label: 'Metric' },
];

const CURRENCIES = [
  { value: 'USD', label: 'USD ($)' },
  { value: 'CAD', label: 'CAD ($)' },
];

const MONETARY_ROUNDINGS = [
  { value: 'round', label: 'Round' },
  { value: 'ceil', label: 'Ceiling' },
  { value: 'floor', label: 'Floor' },
];

const MONETARY_PRECISIONS = [
  { value: 0, label: 'Ones' },
  { value: 3, label: 'Thousands' },
  { value: 6, label: 'Millions' },
];

function toFiscalYearStart(
  month: number | '',
  day: number | '',
): string | null {
  if (month === '' || day === '') return null;
  const mm = String(month).padStart(2, '0');
  const dd = String(day).padStart(2, '0');
  return `1976-${mm}-${dd}`;
}

interface EditOrganizationDialogProps {
  organization: Organization | null;
  onClose: () => void;
}

export function EditOrganizationDialog({
  organization,
  onClose,
}: EditOrganizationDialogProps) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    measurement_system: 'imperial',
    currency: 'USD',
    monetary_precision: 0,
    monetary_rounding: 'round',
  });
  const [fyMonth, setFyMonth] = useState<number | ''>('');
  const [fyDay, setFyDay] = useState<number | ''>('');

  useEffect(() => {
    if (organization) {
      setFormData({
        name: organization.name,
        description: organization.description,
        measurement_system: organization.measurementSystem ?? 'metric',
        currency: organization.currency ?? 'USD',
        monetary_precision: organization.monetaryPrecision ?? 0,
        monetary_rounding: organization.monetaryRounding ?? 'round',
      });
      const parsed = parseFiscalYearStart(organization.fiscalYearStart);
      setFyMonth(parsed.month);
      setFyDay(parsed.day);
    }
  }, [organization]);

  const maxDays =
    fyMonth !== '' ? (MONTHS.find((m) => m.value === fyMonth)?.days ?? 31) : 31;

  // Clamp day if month changes to one with fewer days
  useEffect(() => {
    if (fyDay !== '' && fyDay > maxDays) {
      setFyDay(maxDays);
    }
  }, [fyMonth, maxDays, fyDay]);

  const updateMutation = useMutation({
    mutationFn: async () => {
      if (!organization) return;
      await apiFetch(`/api/organizations/${organization.id}/`, {
        method: 'PATCH',
        body: JSON.stringify({
          ...formData,
          fiscal_year_start: toFiscalYearStart(fyMonth, fyDay),
        }),
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['organizations'] });
      onClose();
    },
  });

  const handleSave = () => {
    void updateMutation.mutate();
  };

  if (!organization) return null;

  return (
    <Dialog open={!!organization} onClose={onClose} maxWidth={'sm'} fullWidth>
      <DialogTitle>Edit Organization</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label={'Name'}
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            fullWidth
            required
          />
          <TextField
            label={'Description'}
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
            fullWidth
            multiline
            rows={4}
          />
          <Typography variant='subtitle2' sx={{ mt: 1 }}>
            Fiscal Year Start Date
          </Typography>
          <Stack direction='row' spacing={2}>
            <FormControl sx={{ minWidth: 160 }}>
              <InputLabel>Month</InputLabel>
              <Select
                value={fyMonth}
                label='Month'
                onChange={(e) => {
                  const val = e.target.value;
                  setFyMonth(val === '' ? '' : Number(val));
                }}
              >
                <MenuItem value=''>
                  <em>None</em>
                </MenuItem>
                {MONTHS.map((m) => (
                  <MenuItem key={m.value} value={m.value}>
                    {m.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 100 }}>
              <InputLabel>Day</InputLabel>
              <Select
                value={fyDay}
                label='Day'
                onChange={(e) => {
                  const val = e.target.value;
                  setFyDay(val === '' ? '' : Number(val));
                }}
                disabled={fyMonth === ''}
              >
                <MenuItem value=''>
                  <em>None</em>
                </MenuItem>
                {Array.from({ length: maxDays }, (_, i) => i + 1).map((d) => (
                  <MenuItem key={d} value={d}>
                    {d}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          <FormControl fullWidth>
            <InputLabel>Measurement System</InputLabel>
            <Select
              value={formData.measurement_system}
              label='Measurement System'
              onChange={(e) =>
                setFormData({
                  ...formData,
                  measurement_system: e.target.value,
                })
              }
            >
              {MEASUREMENT_SYSTEMS.map((m) => (
                <MenuItem key={m.value} value={m.value}>
                  {m.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Currency</InputLabel>
            <Select
              value={formData.currency}
              label='Currency'
              onChange={(e) =>
                setFormData({
                  ...formData,
                  currency: e.target.value,
                })
              }
            >
              {CURRENCIES.map((c) => (
                <MenuItem key={c.value} value={c.value}>
                  {c.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Monetary Display Precision</InputLabel>
            <Select
              value={formData.monetary_precision}
              label='Monetary Display Precision'
              onChange={(e) =>
                setFormData({
                  ...formData,
                  monetary_precision: Number(e.target.value),
                })
              }
            >
              {MONETARY_PRECISIONS.map((p) => (
                <MenuItem key={p.value} value={p.value}>
                  {p.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Monetary Display Rounding</InputLabel>
            <Select
              value={formData.monetary_rounding}
              label='Monetary Display Rounding'
              onChange={(e) =>
                setFormData({
                  ...formData,
                  monetary_rounding: e.target.value,
                })
              }
            >
              {MONETARY_ROUNDINGS.map((r) => (
                <MenuItem key={r.value} value={r.value}>
                  {r.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant={'contained'}
          disabled={updateMutation.isPending}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
}
